<script>
	import range from "lodash/range"
	import pdfjs, { Worker, PDFViewer } from "pdfjs-dist"

	pdfjs.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.3.200/pdf.worker.min.js"

	function getPages(doc, first, last) {
		const allPages = range(first, last + 1).map((number) => doc.pdf.getPage(number))
		return Promise.all(allPages)
	}

	function getDefaults() {
		return {
			pages: [],
			cursor: 0,
		}
	}

	export default {
		name: "PDFDataMultiple",

		props: {
			workspace: {
				required: false,
			},
			workspaceDocuments: {
				type: Array,
			},
			workspaceDocumentsLoaded: {
				type: Boolean,
			},
		},

		data() {
			return {
				current: null,
				initial: true,
				pdfDocs: [],
				loadedCount: 0,
				urlCount: 0,
				firstLoad: false,
				pages: [],
				removedPages: [],
				undoRemoved: [],
				undo: [],
			}
		},

		watch: {
			workspaceDocumentsLoaded() {
				if (this.workspaceDocumentsLoaded) {
					this.getDocuments(this.workspaceDocuments)
				}
			},

			pageCount() {
				this.$bus.$emit("pdf::pages::count", this.pageCount)
			},

			filteredPages: {
				handler(newVal, oldVal) {
					this.$bus.$emit("pdf::workspace::ready", false)
					this.$bus.$emit("pdf::pages::filteredRaw", this.filteredPages)
					this.$bus.$emit("pdf::pages::filtered", this.filteredPages)

					setTimeout(() => {
						this.$bus.$emit("pdf::workspace::ready", true)
					}, 1000)
				},
			},

			removedPages: {
				handler(newVal, oldVal) {
					this.$bus.$emit("pdf::pages::removed", this.removedPages)
				},
				deep: true,
			},
		},

		computed: {
			pageCount() {
				return this.filteredPages.length
			},

			filteredPages() {
				return this.pages.filter((page) => {
					return this.removedPages.filter((removed) => {
						return removed.page_num != page.page_num && removed.document_id != page.id
					})
				})
			},
		},

		methods: {
			getDocuments(urls) {
				pdfjs.disableWorker = true
				this.urlCount = urls.length
				pdfjs.getDocument(urls[this.loadedCount].url).then((pdfDoc_) => {
					this.pdfDocs.push(
						Object.assign(
							{
								pdf: pdfDoc_,
								pdfId: urls[this.loadedCount].id,
							},
							getDefaults()
						)
					)
					this.current = this.loadedCount
					this.fetchPages()
					this.loadedCount++
					if (this.loadedCount !== this.urlCount) {
						return this.getDocuments(urls)
					}
				})
			},

			fetchPages(currentPage = 0) {
				if (!this.pdfDocs.length) return
				if (
					this.pdfDocs[this.current].pdf.numPages > 0 &&
					this.pdfDocs[this.current].pages.length === this.pdfDocs[this.current].pdf.numPages
				)
					return
				const startIndex = this.pdfDocs[this.current].pages.length
				if (this.pdfDocs[this.current].cursor > startIndex) return

				const startPage = startIndex + 1
				const endPage = Math.min(
					Math.max(currentPage, startIndex + this.pdfDocs[this.current].pdf.numPages),
					this.pdfDocs[this.current].pdf.numPages
				)
				this.pdfDocs[this.current].cursor = endPage

				getPages(this.pdfDocs[this.current], startPage, endPage)
					.then((pages) => {
						const deleteCount = 0

						let pagesMapped = pages.map((page) => ({
							page,
							id: this.pdfDocs[this.current].pdfId,
							page_num: page.pageNumber,
							rotation: 0,
							original_rotation: page._pageInfo.rotate,
						}))

						this.pdfDocs[this.current].pages.splice(startIndex, deleteCount, ...pagesMapped)
						this.pages.push(...this.pdfDocs[this.current].pages)
						if (this.loadedCount === this.urlCount && this.firstLoad === false) {
							this.firstLoad = true
							this.handlePageRemove()
							this.handlePageOrder()
						}
						return this.pdfDocs[this.current].pages
					})
					.catch((response) => {
						this.$emit("document-errored", { text: "Failed to retrieve pages", response })
					})
			},

			removePdf(id) {
				const index = this.pdfDocs.findIndex((doc) => {
					return doc.pdfId === id
				})
				if (index != -1) {
					const pdf = this.pdfDocs[index].pages
					this.pages = this.pages.filter((page) => !pdf.includes(page))
					this.pdfDocs.splice(index, 1)
					--this.loadedCount
				}
			},

			handlePageRemove() {
				this.workspace.removed.forEach((removed) => {
					let index = this.pages.findIndex((page) => {
						return page.id == removed.id && page.page_num == removed.page_num
					})
					if (index != -1) {
						this.onRemovePage(index)
					}
				})
			},

			handlePageOrder() {
				let pages = this.workspace.filtered.map((filtered) => {
					let page = this.pages.find((page) => {
						return page.id == filtered.id && page.page_num == filtered.page_num
					}).page
					page._pageInfo.rotate = filtered.rotation != undefined ? filtered.rotation : 0

					return {
						page,
						id: filtered.id,
						page_num: filtered.page_num,
						rotation: filtered.rotation != undefined ? filtered.rotation : 0,
						original_rotation: page._pageInfo.rotate,
					}
				})

				if (pages.length > 0) {
					this.pages = pages
				}
			},

			onRemovePage(index) {
				const pages = this.pages
				this.undo.push([...this.pages])
				if (!this.pages[index]) return
				this.removedPages.push(
					Object.assign({
						id: this.pages[index].id,
						page_num: this.pages[index].page_num,
					})
				)
				pages.splice(index, 1)
				this.undoRemoved.push([...this.removedPages])
				this.pages = pages
			},

			onRemovePages(indexs) {
				const pages = this.pages
				this.undo.push([...this.pages])
				for (var i = indexs.length - 1; i >= 0; i--) {
					if (!pages[indexs[i]]) return
					this.removedPages.push(
						Object.assign({
							id: pages[indexs[i]].id,
							page_num: pages[indexs[i]].page_num,
						})
					)
					pages.splice(indexs[i], 1)
				}
				this.undoRemoved.push([...this.removedPages])
				this.pages = pages
			},

			onPageRendered({ text, page }) {},

			onPageErrored({ text, response, page }) {},

			garbageRemoval() {
				if (!this.undo.length) return
				this.undo = []
				this.undoRemoved = []
			},

			onRestorePages() {
				if (!this.undo.length) return
				let old = this.undo.pop()
				let oldRemoved = this.undoRemoved.pop()
				this.removedPages.pop()
				let restoring = []
				old.forEach((current, index) => {
					if (current.page.destroyed) {
						const sourcePdf = this.pdfDocs.findIndex((doc) => {
							return doc.pdfId == current.id
						})
						const fetchedPage = this.pdfDocs[sourcePdf].pdf.getPage(current.page_num)
						const reloadedPage = fetchedPage.then((page) => {
							old[index].page = page
						})
						restoring.push(old[index].page)
					}
				})
				this.pages = old
				restoring.forEach((restore) => {
					this.$bus.$emit("pdf::page::restore", restore)
				})
			},
		},

		created() {
			this.$bus.$on("pdf::remove", this.removePdf)
			this.$bus.$on("pdf::page::rendered", this.onPageRendered)
			this.$bus.$on("pdf::page::errored", this.onPageErrored)
			this.$bus.$on("pdf::page::remove", this.onRemovePage)
			this.$bus.$on("pdf::pages::remove", this.onRemovePages)
			this.$bus.$on("pdf::pages::restore", this.onRestorePages)
			this.$bus.$on("pdf::pages::fetch", this.fetchPages)
			this.$bus.$on("pdf::garbage::removal", this.garbageRemoval)
		},

		beforeDestroy() {},

		render(h) {
			return h("div", [
				this.$scopedSlots.preview({
					pages: this.pages,
					pdfDocs: this.pdfDocs,
				}),
				this.$scopedSlots.document({
					pages: this.pages,
					pdfDocs: this.pdfDocs,
				}),
			])
		},
	}
</script>
